import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, ListGroup } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { listSpecialties } from '../services/api';

type Specialty = components['schemas']['Specialty'];

/**
 * Category + searchable specialty picker. The taxonomy has ~120 leaves grouped under 10
 * categories, which is too many for a flat dropdown. This component:
 *  - loads the full taxonomy (categories + leaves) once,
 *  - lets the user narrow by category (optional — "All categories" keeps everything),
 *  - offers a type-to-filter search over the (category-scoped) leaves,
 *  - calls onChange with the selected specialtyId (or '' when cleared).
 *
 * Zero extra dependencies: a filtered ListGroup rendered under a search input.
 */
export function SpecialtySelect({
  value,
  onChange,
  placeholder = 'Search specialties…',
  allowEmpty = true,
  emptyLabel = 'Help me choose (admin will triage)',
}: {
  value?: string;
  onChange: (specialtyId: string) => void;
  placeholder?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}) {
  const [all, setAll] = useState<Specialty[]>([]);
  const [categoryId, setCategoryId] = useState<string>('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Full taxonomy (not just the launch subset): doctors may need any specialty.
    listSpecialties(false)
      .then((r) => setAll(r.items))
      .catch(() => setAll([]));
  }, []);

  const categories = useMemo(
    () => all.filter((s) => s.isCategory).sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [all],
  );
  const leaves = useMemo(() => all.filter((s) => !s.isCategory), [all]);

  // The currently selected leaf (to show its name in the input when not actively searching).
  const selected = useMemo(
    () => leaves.find((s) => s.specialtyId === value),
    [leaves, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leaves
      .filter((s) => (categoryId ? s.categoryId === categoryId : true))
      .filter((s) => (q ? s.displayName.toLowerCase().includes(q) : true))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .slice(0, 50);
  }, [leaves, categoryId, query]);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (s: Specialty) => {
    onChange(s.specialtyId);
    setQuery('');
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setQuery('');
    setCategoryId('');
  };

  return (
    <div ref={boxRef}>
      <Form.Select
        className="mb-2"
        value={categoryId}
        onChange={(e) => {
          setCategoryId(e.target.value);
          setOpen(true);
        }}
        aria-label="Specialty category"
      >
        <option value="">All categories</option>
        {categories.map((c) => (
          <option key={c.specialtyId} value={c.specialtyId}>
            {c.displayName}
          </option>
        ))}
      </Form.Select>

      <div className="position-relative">
        <Form.Control
          type="text"
          placeholder={placeholder}
          value={open ? query : selected?.displayName ?? ''}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          aria-label="Specialty"
        />

        {open && (
          <ListGroup
            className="position-absolute w-100 shadow-sm"
            style={{ zIndex: 1000, maxHeight: 260, overflowY: 'auto' }}
          >
            {allowEmpty && (
              <ListGroup.Item action onClick={clear} className="text-muted fst-italic">
                {emptyLabel}
              </ListGroup.Item>
            )}
            {filtered.length === 0 ? (
              <ListGroup.Item disabled className="text-muted">
                No specialties match “{query}”.
              </ListGroup.Item>
            ) : (
              filtered.map((s) => (
                <ListGroup.Item
                  key={s.specialtyId}
                  action
                  active={s.specialtyId === value}
                  onClick={() => pick(s)}
                >
                  {s.displayName}
                </ListGroup.Item>
              ))
            )}
          </ListGroup>
        )}
      </div>
    </div>
  );
}
